import { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ButtonComponent from '../../../components/stories/atoms/button'
import {
  buttonSizeOptions,
  buttonTypeOptions,
  buttonVariantOptions
} from '../../../components/stories/atoms/button/constants'
import { Context } from '../../../components/stories/atoms/icons/essentials'
import colorsOptions from '../../../components/stories/fundamentals/colors-styles/constants'
import ContextMenuComponent from '../../../components/stories/organisms/context-menu'
import {
  contextMenuAlignOptions,
  contextMenuArrowAlignmentOptions,
  contextMenuPaddingOptions,
  contextMenuSideOptions,
  contextMenuTitleSizeOptions,
  contextMenuTitleWeightOptions,
  contextMenuVariantOptions
} from '../../../components/stories/organisms/context-menu/constants'
import { EllipseBold } from '../../../components/stories/atoms/icons/image-overlay'
import { useIntl } from 'react-intl'

const getContextMenuItems = ({
  formatMessage,
  item,
  onCopyProject,
  onLeaveProject
}) => {
  return [
    {
      id: 'copy-project',
      title: formatMessage({
        id: 'projects_copyProject',
        defaultMessage: 'Copy project'
      }),
      action: () => onCopyProject(item),
      hide: !item.canCopyProject || !onCopyProject,
      showDivider: true
    },
    {
      id: 'leave-project',
      title: formatMessage({
        id: 'projects_leaveProject',
        defaultMessage: 'Leave project'
      }),
      action: () => onLeaveProject(item),
      hide: !onLeaveProject,
      variant: contextMenuVariantOptions.DESTRUCTIVE,
      showDivider: false
    }
  ].filter(({ hide }) => !hide)
}

const MyProjectsTableContextMenu = ({
  item,
  onCopyProject,
  onLeaveProject,
  isTileView = false
}) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const iconColor = item.active
    ? colorsOptions.greys['grey-90']
    : colorsOptions.greys['grey-40']

  const handleTriggerClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  const menuItems = useMemo(
    () =>
      getContextMenuItems({
        formatMessage,
        item,
        onCopyProject,
        onLeaveProject
      }),
    [formatMessage, item, onCopyProject, onLeaveProject]
  )

  const tableTriggerButton = (
    <ButtonComponent
      variant={buttonVariantOptions.GHOST}
      buttonType={buttonTypeOptions['ICON-ONLY']}
      disabled={!menuItems.length}
    >
      <Context
        style={{
          fill: iconColor,
          stroke: iconColor,
          strokeWidth: 0
        }}
      />
    </ButtonComponent>
  )

  const tilesTriggerButton = (
    <ButtonComponent
      buttonType={buttonTypeOptions['ICON-ONLY']}
      variant={buttonVariantOptions['IMAGE-OVERLAY']}
      size={buttonSizeOptions.SMALL}
    >
      <EllipseBold stroke={colorsOptions.greys.white} />
    </ButtonComponent>
  )

  const triggerRef = useRef(null)
  const trigger = isTileView ? tilesTriggerButton : tableTriggerButton

  if (!menuItems.length) {
    return trigger
  }

  const handleItemClick = (action) => (event) => {
    event.stopPropagation()
    setIsOpen(false)
    action()
  }

  const triggerRect =
    isOpen && triggerRef.current
      ? triggerRef.current.getBoundingClientRect()
      : null

  // Check if the context menu overlaps the bottom limit of the screen
  const MENU_ESTIMATED_HEIGHT = 250
  const openUpward = triggerRect
    ? window.innerHeight - triggerRect.bottom < MENU_ESTIMATED_HEIGHT
    : false

  return (
    <div ref={triggerRef}>
      <ContextMenuComponent.Root isOpen={isOpen} setIsOpen={setIsOpen}>
        <ContextMenuComponent.Trigger callback={handleTriggerClick}>
          {trigger}
        </ContextMenuComponent.Trigger>
        {triggerRect &&
          createPortal(
            <div onMouseDown={(e) => e.stopPropagation()}>
              <ContextMenuComponent.List
                width="200px"
                align={contextMenuAlignOptions.START}
                side={
                  openUpward
                    ? contextMenuSideOptions.TOP
                    : contextMenuSideOptions.BOTTOM
                }
                arrow
                arrowAlignment={contextMenuArrowAlignmentOptions.END}
                style={{
                  position: 'fixed',
                  top: openUpward ? 'auto' : triggerRect.bottom,
                  bottom: openUpward
                    ? window.innerHeight - triggerRect.top
                    : 'auto',
                  right: window.innerWidth - triggerRect.right,
                  left: 'auto',
                  transform: 'none',
                  zIndex: 9999
                }}
              >
                {menuItems.map(
                  ({ action, id, title, variant, showDivider }) => (
                    <Fragment key={id}>
                      <ContextMenuComponent.Item
                        id={id}
                        variant={variant}
                        padding={contextMenuPaddingOptions['EXTRA-LARGE']}
                        titleWeight={contextMenuTitleWeightOptions['TEXT-600']}
                        titleSize={contextMenuTitleSizeOptions['TEXT-R']}
                        onClick={handleItemClick(action)}
                        title={title}
                      />
                      {showDivider && <ContextMenuComponent.Divider />}
                    </Fragment>
                  )
                )}
              </ContextMenuComponent.List>
            </div>,
            document.body
          )}
      </ContextMenuComponent.Root>
    </div>
  )
}

export default MyProjectsTableContextMenu
